import React from "react";
import PropTypes from "prop-types";
import range from "just-range";
import classNames from "classnames";
import { connect } from "react-redux";
import Key from "./Key";
import MidiNumbers from "./MidiNumbers";
import {
  highlightVoiceKey,
  setSustainPedal,
  setTonesMT,
  setVoice,
  setSustainedNotes
} from "./store/actions";
import Audio from "./audio";
import { standardLabels } from "./helpers/keyLabelsMapper";

class Keyboard extends React.Component {
  constructor(props) {
    super(props);
    // Initialize an array to hold refs for each child component
    this.childRefs = [];
    this.tuner = new Audio(props.sliderValue);
    this.component = props;
    //   this.utils = new Utils();
    this.stopped = false;
  }

  addRef = (ref) => {
    if (ref && !this.childRefs.includes(ref)) {
      this.childRefs.push(ref);
    }
  };

  callChildToPlayFunctions = (midiNumber) => {
    // Call the method on each child component
    this.childRefs.forEach((childRef) => {
      childRef.playExternalMidi(midiNumber);
    });
  };

  callChildToStopFunctions = (midiNumber) => {
    // Call the method on each child component
    this.childRefs.forEach((childRef) => {
      childRef.stopExternalMidi(midiNumber);
    });
  };

  callChildToPlayKeyboardFunctions = (midiNumber) => {
    // Call the method on each child component
    this.childRefs.forEach((childRef) => {
      childRef.playFromKeyboard(midiNumber);
    });
  };

  callChildToStopKeyboardFunctions = (midiNumber) => {
    // Call the method on each child component
    this.childRefs.forEach((childRef) => {
      childRef.stopFromKeyboard(midiNumber);
    });
  };

  static propTypes = {
    activeNotes: PropTypes.arrayOf(PropTypes.number),
    onPlayNoteInput: PropTypes.func.isRequired,
    onStopNoteInput: PropTypes.func.isRequired,
    stopAllNotes: PropTypes.func,
    renderNoteLabel: PropTypes.func.isRequired,
    className: PropTypes.string,
    disabled: PropTypes.bool,
    gliss: PropTypes.bool,
    useTouchEvents: PropTypes.bool,
    width: PropTypes.number,
    sustainPedal: PropTypes.bool,
    setSustainPedal: PropTypes.func.isRequired,
  };

  static defaultProps = {
    disabled: false,
    gliss: false,
    useTouchEvents: false,
    renderNoteLabel: () => {},
    sustainPedal: false,
  };

  handleSustainPadel = () => {
    // before turning paddle on, set the current active notes into the sustain paddle notes set
    if(!this.props.sustainPedal){
      if(this.props.activeNotes && this.props.activeNotes.length > 0){
        let tempSet = new Set();
        this.props.activeNotes.map((midiNum)=>{
          tempSet.add(midiNum)
        })
        this.props.setSustainedNotes(tempSet);
      }
    }
      this.props.setSustainPedal(!this.props.sustainPedal);

  };

  // Range of midi numbers on keyboard
  getMidiNumbers() {
    return range(
      this.props.config.noteRange.first,
      this.props.config.noteRange.last + 1
    );
  }

  getNaturalKeyCount() {
    return this.getMidiNumbers().filter((number) => {
      const { isAccidental } = MidiNumbers.getAttributes(number);
      return !isAccidental;
    }).length;
  }

  // Returns a ratio between 0 and 1
  getNaturalKeyWidth() {
    return 1 / this.getNaturalKeyCount();
  }

  // getWidth() {
  //   return this.props.width ? this.props.width : "100%";
  // }

  // getHeight() {
  //   if (!this.props.width) {
  //     return "100%";
  //   }
  //   const keyWidth = this.props.width * this.getNaturalKeyWidth();
  //   return `${keyWidth / this.props.keyWidthToHeight}px`;
  // }

  //  Label the keys below

  renderNoteLabel = ({ midiNumber, isActive, isAccidental }) => {
    let keyboardLabels = this.props.standardLabels[
      Object.keys(this.props.standardLabels).filter((key) => key == midiNumber)
    ]
      ? this.props.standardLabels[
          Object.keys(this.props.standardLabels).filter(
            (key) => key == midiNumber
          )
        ]
      : null;
    if (keyboardLabels) {
      return (
        <div
          className={classNames("ReactPiano__NoteLabel", {
            "ReactPiano__NoteLabel--active": isActive,
            "ReactPiano__NoteLabel--accidental": isAccidental,
            "ReactPiano__NoteLabel--natural": !isAccidental,
          })}
        >
          {keyboardLabels}
        </div>
      );
    } else {
      return null;
    }
  };

  // set up external Midi

  setEnableMidi = () => {
    if (navigator.requestMIDIAccess) {
      console.log(
        "This browser supports MIDI! If your MIDI device does not work reconnect it and restart MIDI from Settings!"
      );
      // navigator.requestMIDIAccess().then(this.onMIDISuccess, this.onMIDIFailure);
      let self = this;
      navigator
        .requestMIDIAccess()
        .then((e) => {
          if (self.globalMidiAccess) {
            for (var input of self.globalMidiAccess.inputs.values()) {
              input.onmidimessage = null;
              input.onstatechange = function (e) {
                if (e.target.state !== "connected") {
                  e.target.onmidimessage = null;
                }
              };
            }
          }
          self.globalMidiAccess = e;
          self.onMIDISuccess(self.globalMidiAccess);
        })
        .catch((e) => {
          this.onMIDIFailure();
        });
    } else {
      console.log(
        "MIDI is not supported in this browser! Try it on Google Chrome"
      );
    }
  };

  onMIDISuccess = (midiAccess) => {
    for (var input of midiAccess.inputs.values()) {
      input.onmidimessage = this.getMIDIMessage;
      let self = this;
      input.onstatechange = function (e) {
        if (e.target.state === "disconnected") {
          e.target.onmidimessage = null;
        }
        if (e.target.state === "connected") {
          e.target.onmidimessage = self.getMIDIMessage;
        }
      };
    }
  };

  onMIDIFailure = () => {
    console.log(
      "Could not access MIDI device. Connect a device and restart MIDI from Settings!"
    );
  };

  getMIDIMessage = (message) => {
    var command = message.data[0];
    if (command === 248 || command === 254) {
      return;
    }
    var note = message.data[1];
    var valV = message.data.length > 2 ? message.data[2] : 0; // a valV value might not be included with a noteOff command

    if (command >= 144 && command <= 159) {
      if (valV > 0) {
        if (this.props.enableMidiFlag) {
          this.callChildToPlayFunctions(note);
        }
      } else {
        // console.log('This Note is not found in data variable so off =======> ',note);
        this.callChildToStopFunctions(note);
      }
    } else if (command >= 128 && command <= 143) {
      this.callChildToStopFunctions(note);
    } else {
      // console.log('NOTE NOT FOUND');
    }
  };

  // Audio setup for voice pitch

  start() {
    const self = this;
    self.stopped = false;
    this.tuner.onNoteDetected = function (note) {
      if (self.props.voiceConsistency) {
        if (self.lastNote === note.name) {
          self.update(note);
        } else {
          self.lastNote = note.name;
        }
      } else {
        self.update(note);
      }
    };

    self.tuner.init();
    self.frequencyData = new Uint8Array(self.tuner.analyser.frequencyBinCount);
  }

  stop() {
    this.stopped = true;
    if (this.tuner.stream) {
      this.tuner.stream.getTracks().forEach(function (track) {
        track.stop();
        // document.gauges[0].value = 75;
      });
      this.tuner.stream = null;
      this.tuner.scriptProcessor.disconnect();
    }

    // tunerUtils = new AudioTuner(tunerUtils.component);
    this.tuner = new Audio(this.props.sliderValue);
    // document.getElementById("svgPath").style.fill = "#c5a73b";
    // document.querySelector(".animate-tuner").style.transform = "rotate(0deg)";

    // tunerUtils.utils.resetPianoKeys();
    this.props.highlightVoiceKey(0);
  }

  //   animateTuner(note) {
  //     let rotation = (note.cents / 50) * 45;
  //     let animationCenter = document.querySelector(".animate-tuner");

  //     if (rotation < 10 && rotation > -10)
  //       document.getElementById("svgPath").style.fill = "#155e15";
  //     else if (rotation > 0)
  //       document.getElementById("svgPath").style.fill = "#3232bb";
  //     else if (rotation < 0)
  //       document.getElementById("svgPath").style.fill = "#a02121";

  //     animationCenter.style.transform = "rotate(" + rotation + "deg)";
  //   }

  highlightPianoKey(note, bridgeColor = "") {
    //   this.utils.resetPianoKeys();
    console.log(note);
    let a = Object.keys(standardLabels).filter(
      (key) => standardLabels[key] == note
    );
    this.props.highlightVoiceKey(a);

    if (this.props.highlightkeyNumberForVoice?.includes(a[0])) {
      const noteKeySelector = `.ReactPiano__Key[data-midi="${a}"]`;
      const noteElement = document.querySelector(noteKeySelector);

      if (noteElement) {
        // Check if the noteElement represents an accidental note
        const isAccidental = noteElement.classList.contains(
          "ReactPiano__Key--accidental"
        );

        // Helper function to remove and reapply a class for blinking effect
        const resetClass = (element, className) => {
          element.classList.remove(className);
          void element.offsetHeight; // Trigger reflow
          element.classList.add(className);
        };

        // Handle Lowest Note (Wrong Input)
        if (
          this.props.tonesMT.modesOfScalesValues?.name !== "" &&
          !this.props.musicTheoryMidiNums.includes(Number(a[0])) &&
          ((this.props.enableMidiFlag && this.props.showWrongMidiInput) ||
            (this.props.voiceDetect && this.props.showWrongMicInput))
        ) {
          console.log(a);
          const className = isAccidental
            ? "ReactPiano__Key--lowestNote-accidental"
            : "ReactPiano__Key--lowestNote";
          resetClass(noteElement, className);
          return className;
        }

        // Handle Bridge Note
        if (a[0] < this.props.notesMidiNum.bridgeVoiceNote[1]) {
          const className = isAccidental
            ? "ReactPiano__Key--bridgeNote-accidental"
            : "ReactPiano__Key--bridgeNote";
          resetClass(noteElement, className);
          return className;
        }

        // Handle Highest Note
        if (a[0] >= this.props.notesMidiNum.bridgeVoiceNote[1]) {
          const className = isAccidental
            ? "ReactPiano__Key--highestNote-accidental"
            : "ReactPiano__Key--highestNote";
          resetClass(noteElement, className);
          return className;
        }

        // Handle Voice Highlight
        const voiceHighlightClass = isAccidental
          ? "ReactPiano__Key--voiceHighlight-accidental"
          : "ReactPiano__Key--voiceHighlight";

        if (noteElement.classList.contains("ReactPiano__Key--voiceHighlight")) {
          resetClass(noteElement, voiceHighlightClass);
        } else {
          const otherVoiceHighlightClass = isAccidental
            ? "ReactPiano__Key--voiceHighlight"
            : "ReactPiano__Key--voiceHighlight-accidental";
          noteElement.classList.remove(otherVoiceHighlightClass);
          resetClass(noteElement, voiceHighlightClass);
        }
        return voiceHighlightClass;
      }
    }

    // WRITE HIGHLIGHT LOGIC HERE

    // let pianoKey = document.querySelector(
    //   `.react-piano-key[data-note="${note}"]`
    // );
    // if (pianoKey && pianoKey.classList.contains("midi-played")) {
    //   pianoKey.children[1].classList.add("highlighted");
    // } else {
    //   if (pianoKey && bridgeColor === "")
    //     pianoKey.classList.add("react-piano-key-highlighted");
    //   else if (pianoKey && bridgeColor !== "") {
    //     pianoKey.classList.add("react-piano-key-highlighted");
    //     pianoKey.classList.add(bridgeColor);
    //   }
    // }
  }

  update(tempNote) {
    if (this.stopped) return;

    // let tones = this.component.props.tones;
    let note = tempNote.name + tempNote.octave;
    let frequency = tempNote.frequency.toFixed(2);

    // // this.animateTuner(tempNote);

    // // Different shade based on bridge values
    // if (tones.validNotes) {
    //   let indexCurrentNote = tones.validNotes.findIndex((x) => x === note);
    //   if (
    //     indexCurrentNote >= tones.indexBridgeStart &&
    //     indexCurrentNote <= tones.indexBridgeEnd
    //   ) {
    //     this.highlightPianoKey(note, "bridge");
    //   } else {
    //     this.highlightPianoKey(note);
    //   }
    // } else {
    this.highlightPianoKey(note);
    // }

    // this.component.setState({ note, frequency });
    // this.setState({ note, frequency });
  }

  componentDidMount() {
    if (this.props.enableMidiFlag) {
      this.setEnableMidi();
    }
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.voiceDetect == true &&
      this.props.voiceDetect !== prevProps.voiceDetect
    ) {
      this.start();
    } else if (
      this.props.voiceDetect == false &&
      this.props.voiceDetect !== prevProps.voiceDetect
    ) {
      this.stop();
    }
    // update gain for mic sensitivity
    if (
      this.props.sliderValue &&
      this.props.sliderValue !== prevProps.sliderValue
    ) {
      if (this.tuner.gainNode) {
        this.tuner.gainNode.gain.value = this.props.sliderValue;
      }
    }
    if (
      prevProps.enableMidiFlag !== this.props.enableMidiFlag &&
      this.props.enableMidiFlag
    ) {
      this.setEnableMidi();
    }
  }

  componentWillUnmount() {
    this.stop();
    this.props.setVoice(false);
  }

  render() {
    const naturalKeyWidth = this.getNaturalKeyWidth();
    return (
      <div>
        <piano-btn-container>
          <div className="piano-wrapper">
            <div
              className={classNames("ReactPiano__Keyboard", this.props.className)}
            >
              {this.getMidiNumbers().map((midiNumber) => {
                const { note, isAccidental } = MidiNumbers.getAttributes(midiNumber);
                const isActive = !this.props.disabled && this.props.activeNotes.includes(midiNumber);
                return (
                  <Key
                    naturalKeyWidth={naturalKeyWidth}
                    midiNumber={midiNumber}
                    active={isActive}
                    accidental={isAccidental}
                    disabled={this.props.disabled}
                    onPlayNoteInput={this.props.onPlayNoteInput}
                    onStopNoteInput={this.props.onStopNoteInput}
                    gliss={this.props.gliss}
                    useTouchEvents={this.props.useTouchEvents}
                    key={midiNumber}
                    ref={this.addRef}
                  >
                    {this.props.disabled
                      ? null
                      : this.renderNoteLabel({
                          isActive,
                          isAccidental,
                          midiNumber,
                        })}
                  </Key>
                );
              })}
            </div>
            <div className="piano-pedal-wrapper">
              <button 
                className={`sustain-pedal ${this.props.sustainPedal ? 'active' : ''}`}
                onClick={this.handleSustainPadel}
              >
                Sustain: {this.props.sustainPedal ? 'On' : 'Off'}
              </button>
            </div>
          </div>
        </piano-btn-container>
      </div>
    );
  }
}

// function isNaturalMidiNumber(value) {
//   if (typeof value !== "number") {
//     return false;
//   }
//   return MidiNumbers.NATURAL_MIDI_NUMBERS.includes(value);
// }

// function noteRangePropType(props, propName, componentName) {
//   const { first, last } = props[propName];
//   if (!first || !last) {
//     return new Error(
//       `Invalid prop ${propName} supplied to ${componentName}. ${propName} must be an object with .first and .last values.`
//     );
//   }
//   if (!isNaturalMidiNumber(first) || !isNaturalMidiNumber(last)) {
//     return new Error(
//       `Invalid prop ${propName} supplied to ${componentName}. ${propName} values must be valid MIDI numbers, and should not be accidentals (sharp or flat notes).`
//     );
//   }
//   if (first >= last) {
//     return new Error(
//       `Invalid prop ${propName} supplied to ${componentName}. ${propName}.first must be smaller than ${propName}.last.`
//     );
//   }
// }

const mapStateToProps = (state) => ({
  config: state.config,
  voiceDetect: state.voiceDetect,
  sliderValue: state.sliderValue,
  voiceConsistency: state.voiceConsistency,
  enableMidiFlag: state.enableMidiFlag,
  standardLabels: state.standardLabels,
  highlightkeyNumberForVoice: state.highlightkeyNumberForVoice,
  showWrongMicInput: state.showWrongMicInput,
  showWrongMidiInput: state.showWrongMidiInput,
  tonesMT: state.tonesMT,
  musicTheoryMidiNums: state.musicTheoryMidiNums,
  notesMidiNum: state.notesMidiNum,
  sustainPedal: state.sustainPedal,
});

const mapDispatchToProps = (dispatch) => ({
  highlightVoiceKey: (value) => dispatch(highlightVoiceKey(value)),
  setVoice: (value) => dispatch(setVoice(value)),
  setTonesMT: (value) => dispatch(setTonesMT(value)),
  setSustainPedal: (value) => dispatch(setSustainPedal(value)),
  setSustainedNotes: (value) => dispatch(setSustainedNotes(value)),
});

export default connect(mapStateToProps, mapDispatchToProps, null, {
  forwardRef: true,
})(Keyboard);
